/**
 * Field Validation Utilities for Correspondence
 *
 * Standalone utilities for validating template fields against matter context and overrides.
 * This logic mirrors the backend's field_utils.py to ensure frontend and backend
 * always evaluate missing fields identically.
 *
 * Backend parity: app/domain/correspondence/field_utils.py
 */
import type { MissingFieldInfo as ApiMissingFieldInfo } from '@/services/api/correspondenceService';
import type { ReadTemplateField } from '@/services/api/generated/correspondenceService';

// ============================================================================
// Types
// ============================================================================

export type TemplateField = ReadTemplateField;

export interface MissingFieldInfo {
  fieldId: string;
  fieldName: string;
  fieldSource: string;
  contextKey: string | null;
}

export type MatterContext = Record<string, string | null | undefined>;

/** Source of a field's resolved value */
export type FieldValueSource = 'override' | 'context' | 'default' | 'empty';

export interface FieldValueInfo {
  value: string;
  source: FieldValueSource;
}

// ============================================================================
// API Response Normalization
// ============================================================================

/**
 * Convert API MissingFieldInfo (snake_case) to local format (camelCase).
 */
export function normalizeApiMissingFields(
  apiFields: Record<string, ApiMissingFieldInfo> | undefined,
): Record<string, MissingFieldInfo> {
  if (!apiFields) return {};
  return Object.entries(apiFields).reduce<Record<string, MissingFieldInfo>>(
    (acc, [key, value]) => ({
      ...acc,
      [key]: {
        fieldId: value.field_id,
        fieldName: value.field_name,
        fieldSource: value.field_source,
        contextKey: value.context_key ?? null,
      },
    }),
    {},
  );
}

// ============================================================================
// Field Value Resolution (mirrors backend resolve_field_value)
// ============================================================================

/**
 * Get the override key for a field.
 * V2 fields use `reference` as the primary key, with `context_key` as fallback.
 * Overrides are keyed by context_key or reference, NOT by field.id (UUID).
 */
export function getOverrideKey(field: TemplateField): string | null {
  // Prefer context_key (maps to MatterContextField enum on backend)
  if (field.context_key) {
    return field.context_key;
  }
  // Fall back to reference (the field's placeholder name)
  if (field.reference) {
    return field.reference;
  }
  return null;
}

/**
 * Resolve field value with source tracking.
 * Precedence: override (by context key/reference) → context → default → empty
 *
 * Backend parity: app/domain/correspondence/field_utils.py::resolve_field_value
 */
export function getFieldValueInfo(
  field: TemplateField,
  context: MatterContext,
  overrides: Record<string, string>,
): FieldValueInfo {
  const overrideKey = getOverrideKey(field);

  // 1. Check explicit overrides first
  if (overrideKey && overrideKey in overrides) {
    const overrideValue = overrides[overrideKey];
    if (overrideValue !== undefined && overrideValue !== '') {
      return { value: overrideValue, source: 'override' };
    }
  }

  // 2. Check reference in context
  if (field.reference) {
    const refValue = context[field.reference];
    if (refValue !== null && refValue !== undefined && refValue !== '') {
      return { value: String(refValue), source: 'context' };
    }
  }

  // 3. Check context key in context
  if (field.context_key) {
    const ctxValue = context[field.context_key];
    if (ctxValue !== null && ctxValue !== undefined && ctxValue !== '') {
      return { value: String(ctxValue), source: 'context' };
    }
  }

  // 4. Check default value
  if (field.default_value) {
    return { value: field.default_value, source: 'default' };
  }

  return { value: '', source: 'empty' };
}

/**
 * Resolve field value (convenience wrapper returning just the value)
 */
export function resolveFieldValue(
  field: TemplateField,
  context: MatterContext,
  overrides: Record<string, string>,
): string {
  return getFieldValueInfo(field, context, overrides).value;
}

/**
 * Get display name for a template field
 */
export function getFieldDisplayName(field: TemplateField): string {
  return (
    field.display_name ||
    field.reference ||
    field.context_key ||
    'Custom field'
  );
}

/**
 * Check if a field value is considered missing/empty
 */
export function isFieldValueMissing(value: string): boolean {
  return !value || value.trim() === '';
}

/**
 * Check if a field is required
 */
export function isFieldRequired(field: TemplateField): boolean {
  return field.required;
}
